import React from "react";
import { DEVICE_COLORS } from "./reportCatalog";
import { formatDuration, DOWNTIME_SOURCE } from "./reportData";

// Presentation-only templates. Every prop is already computed real data —
// no stubs, no fetching in here.

const colorFor = (i) => DEVICE_COLORS[i % DEVICE_COLORS.length];

export function DeviceLegend({ names }) {
    if (!names || names.length <= 1) return null;
    return (
        <div className="rp1-device-legend">
            {names.map((n, i) => (
                <span key={n}>
                    <span className="rp1-device-dot" style={{ background: colorFor(i) }} />
                    {n}
                </span>
            ))}
        </div>
    );
}

export function EmptyState({ children }) {
    return <div className="rp1-empty">{children}</div>;
}

// rows: [{ name, actual, target }]
export function BulletRows({ rows, showTitle = true }) {
    if (!rows.length) return <EmptyState>No devices selected.</EmptyState>;
    const max = Math.max(...rows.map((r) => Math.max(r.actual, r.target || 0)), 1) * 1.08;
    const anyTarget = rows.some((r) => r.target > 0);
    return (
        <>
            {showTitle && <div className="rp1-viz-title">Actual vs target by machine</div>}
            {anyTarget ? (
                <div className="rp1-target-legend"><span className="tick" /> Target marker</div>
            ) : (
                <div className="rp1-target-legend">
                    No targets set — add target units on Job Schedules to see variance.
                </div>
            )}
            {rows.map((r) => {
                const pct = r.target > 0 ? Math.round((r.actual / r.target) * 100) : null;
                return (
                    <div className="rp1-target-row" key={r.name}>
                        <div className="rp1-target-label" title={r.name}>{r.name}</div>
                        <div className="rp1-target-track">
                            <div className="rp1-target-fill" style={{ width: `${(r.actual / max) * 100}%` }} />
                            {r.target > 0 && (
                                <div className="rp1-target-marker" style={{ left: `${(r.target / max) * 100}%` }} />
                            )}
                        </div>
                        <div className="rp1-target-val">
                            {r.target > 0 ? `${r.actual} / ${r.target}` : `${r.actual} parts`}
                            {pct !== null && (
                                <span className={`rp1-pct-pill ${r.actual >= r.target ? "good" : "bad"}`}>
                                    {pct}%
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </>
    );
}

// kpis: [{ label, value, sub, subClass }] ; rows for BulletRows
export function ProductionKpiTemplate({ kpis, rows }) {
    return (
        <>
            <div className="rp1-kpi-grid">
                {kpis.map((k) => (
                    <div className="rp1-kpi-card" key={k.label}>
                        <div className="k-label">{k.label}</div>
                        <div className="k-val">{k.value}</div>
                        {k.sub && <div className={`k-sub ${k.subClass || ""}`}>{k.sub}</div>}
                    </div>
                ))}
            </div>
            <div className="rp1-viz-title">Machine output vs target</div>
            <BulletRows rows={rows} showTitle={false} />
        </>
    );
}

// rows: [{ name, cells: [{ label, value, display }] }]; higher = better unless invert
export function HeatmapTemplate({ rows, dayLabels, title, invert = false }) {
    if (!rows.length) return <EmptyState>No devices selected.</EmptyState>;
    const values = rows.flatMap((r) => r.cells.map((c) => c.value)).filter((v) => v != null);
    const max = Math.max(...values, 1);
    const cellClass = (v) => {
        if (v == null) return "";
        let ratio = v / max;
        if (invert) ratio = 1 - ratio;
        return ratio >= 0.9 ? "h1" : ratio >= 0.7 ? "h2" : ratio >= 0.5 ? "h3" : ratio >= 0.3 ? "h4" : "h5";
    };
    return (
        <>
            <div className="rp1-viz-title">{title}</div>
            <div className="rp1-heatmap-wrap">
                <table className="rp1-heatmap">
                    <thead>
                        <tr>
                            <th>Machine</th>
                            {dayLabels.map((d) => <th key={d} style={{ textAlign: "center" }}>{d}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.name}>
                                <th>{r.name}</th>
                                {r.cells.map((c, i) => (
                                    <td key={i} className={cellClass(c.value)}>
                                        {c.value == null ? "—" : c.display}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

// events: [{ key, deviceName, deviceIndex, startLabel, windowLabel, duration,
//            source, reasonName, persistedId }]
// reasons: [{ id, name }] ; onTag(event, reasonId)
export function TimelineTemplate({ events, deviceNames, reasons, onTag, tagging }) {
    return (
        <>
            <div className="rp1-viz-title">Event chronology</div>
            <DeviceLegend names={deviceNames} />
            <div className="rp1-timeline">
                {events.length === 0 && (
                    <div className="rp1-event">No downtime detected in the selected window.</div>
                )}
                {events.map((e) => (
                    <div
                        className="rp1-event"
                        key={e.key}
                        style={{ "--rp1-event-dot": colorFor(e.deviceIndex) }}
                    >
                        <div className="rp1-event-head">
                            <span>
                                {e.deviceName} — {e.source === DOWNTIME_SOURCE.SILENCE ? "No telemetry" : "Machine stopped"}
                            </span>
                            <span className="rp1-duration-pill">{formatDuration(e.duration)}</span>
                        </div>
                        <div className="rp1-event-meta">{e.windowLabel}</div>
                        <div className="rp1-event-tag">
                            {e.reasonName ? (
                                <span className="rp1-reason-chip">{e.reasonName}</span>
                            ) : (
                                <select
                                    defaultValue=""
                                    disabled={tagging}
                                    onChange={(ev) => ev.target.value && onTag(e, parseInt(ev.target.value))}
                                >
                                    <option value="" disabled>Tag reason…</option>
                                    {reasons.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

// buckets: [{ label, seconds, pct, cumPct }] sorted desc
export function ParetoTemplate({ buckets, totalSeconds }) {
    if (!buckets.length) {
        return (
            <EmptyState>
                No tagged downtime in this window. Tag events from the Downtime Report
                to build the Pareto.
            </EmptyState>
        );
    }
    const maxPct = Math.max(...buckets.map((b) => b.pct), 1);
    return (
        <>
            <div className="rp1-viz-title">
                Downtime contribution by reason — total {formatDuration(totalSeconds)}
            </div>
            <div className="rp1-pareto">
                {buckets.map((b) => (
                    <div className="rp1-pareto-col" key={b.label}>
                        <div className="rp1-pareto-bar" style={{ height: `${(b.pct / maxPct) * 160}px` }}>
                            <span>{b.pct}%</span>
                        </div>
                        <div className="rp1-pareto-label">{b.label}</div>
                    </div>
                ))}
                <div className="rp1-pareto-line">
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                        <polyline
                            points={buckets
                                .map((b, i) => `${(100 / buckets.length) * (i + 0.5)},${100 - b.cumPct * 0.94}`)
                                .join(" ")}
                            fill="none"
                            stroke="#182033"
                            strokeWidth="1.8"
                        />
                    </svg>
                </div>
            </div>
        </>
    );
}

// stats: [{ label, value, sub }]
export function MaintSummaryTemplate({ stats, rows }) {
    return (
        <>
            <div className="rp1-kpi-grid">
                {stats.map((s) => (
                    <div className="rp1-kpi-card" key={s.label}>
                        <div className="k-label">{s.label}</div>
                        <div className="k-val">{s.value}</div>
                        {s.sub && <div className="k-sub">{s.sub}</div>}
                    </div>
                ))}
            </div>
            <div className="rp1-viz-title">Schedules by machine</div>
            {rows.length === 0 && <EmptyState>No maintenance schedules for the selected devices.</EmptyState>}
            <div className="rp1-checklist">
                {rows.map((r) => (
                    <div className="rp1-check-row" key={r.key}>
                        <div className="rp1-check-box" />
                        <div className="rp1-check-title">
                            {r.title}
                            <small>{r.meta}</small>
                        </div>
                        <div>{r.window}</div>
                        <div>
                            <span className={`rp1-status-pill ${r.statusClass}`}>{r.statusLabel}</span>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

// items: [{ key, title, meta, window, statusLabel, statusClass }]
export function ChecklistTemplate({ items }) {
    return (
        <>
            <div className="rp1-viz-title">Planned maintenance</div>
            {items.length === 0 && (
                <EmptyState>No planned maintenance scheduled for the selected devices.</EmptyState>
            )}
            <div className="rp1-checklist">
                {items.map((it) => (
                    <div className="rp1-check-row" key={it.key}>
                        <div className="rp1-check-box" />
                        <div className="rp1-check-title">
                            {it.title}
                            <small>{it.meta}</small>
                        </div>
                        <div>{it.window}</div>
                        <div>
                            <span className={`rp1-status-pill ${it.statusClass}`}>{it.statusLabel}</span>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

// series: [{ name, points: [number|null] }], labels: x-axis labels
export function TrendTemplate({ series, labels, title, note }) {
    const flat = series.flatMap((s) => s.points).filter((v) => v != null);
    if (!flat.length) {
        return <EmptyState>No analog datapoints in the selected window.</EmptyState>;
    }
    const max = Math.max(...flat, 1);
    const W = 700;
    const H = 250;
    const toPolyline = (points) => {
        const step = points.length > 1 ? W / (points.length - 1) : W;
        return points
            .map((v, i) => (v == null ? null : `${i * step},${H - (v / max) * (H - 20)}`))
            .filter(Boolean)
            .join(" ");
    };
    return (
        <>
            <div className="rp1-viz-title">{title}</div>
            <DeviceLegend names={series.map((s) => s.name)} />
            <div className="rp1-trend-chart">
                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                    {series.map((s, i) => (
                        <polyline
                            key={s.name}
                            points={toPolyline(s.points)}
                            fill="none"
                            stroke={series.length > 1 ? colorFor(i) : "var(--cat-color)"}
                            strokeWidth="3"
                        />
                    ))}
                </svg>
            </div>
            <div className="rp1-trend-labels">
                {labels.map((l) => <span key={l}>{l}</span>)}
            </div>
            {note && <div className="rp1-trend-note">{note}</div>}
        </>
    );
}

export function DowntimeTable({ events }) {
    if (!events.length) return null;
    return (
        <>
            <table className="rp1-data-table">
                <thead>
                    <tr>
                        <th>Machine</th>
                        <th>Type</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Duration</th>
                        <th>Reason</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map((e) => (
                        <tr key={e.key}>
                            <td>{e.deviceName}</td>
                            <td>{e.source === DOWNTIME_SOURCE.SILENCE ? "No telemetry" : "Stopped"}</td>
                            <td>{e.startLabel}</td>
                            <td>{e.endLabel}</td>
                            <td>{formatDuration(e.duration)}</td>
                            <td>{e.reasonName || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="rp1-table-foot">
                <span>Showing 1–{events.length} of {events.length}</span>
            </div>
        </>
    );
}
